import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {LandingPageService} from '../../../../services/landing-page.service';
import {Globals} from '../../../../globals';
import {PatientCaseViewDataModel} from '../../../../models/patient/patient-case-view-data.model';
import {FileManagerService} from '../../../../modules/file-manager/file-manager.service';
import {DocumentInFolderDataModel} from '../../../../models/document/document-in-folder-data.model';
import {DocumentGetCaseTeethModel} from '../../../../models/document/document-get-case-teeth.model';
import {ToothConfigurationModel} from '../../../../models/teeth/tooth-configuration.model';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {DataPatientCaseWorkItemToothModel} from '../../../../models/teeth/data-patient-case-work-item-tooth.model';
import {DocumentPseudoPatientCaseWorkItemToothDataModel} from '../../../../models/document/document-pseudo-patient-case-work-item-tooth-data.model';
import {DocumentSaveCaseTeethModel, SelectedCaseWorkItemTeeth} from '../../../../models/document/document-save-case-teeth.model';
import {forkJoin} from 'rxjs';
import {finalize} from 'rxjs/operators';
import { UiService } from 'src/app/services/ui.service';

@Component({
  standalone: false,
  selector: 'app-tdo-organizer-case-tooth-dialog',
  templateUrl: './tdo-organizer-case-tooth-dialog.component.html',
  styleUrls: ['./tdo-organizer-case-tooth-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TdoOrganizerCaseToothDialogComponent implements OnInit {
  cases: PatientCaseViewDataModel[] = [];
  selectedCase: PatientCaseViewDataModel | null = null;
  currentCaseAndTeeth: DocumentGetCaseTeethModel;
  selectedConfigurationTeeth: ToothConfigurationModel[] = [];
  selectedCaseWorkItemTeeth: DataPatientCaseWorkItemToothModel[] = [];
  selectedCaseWorkItemTeethIds: SelectedCaseWorkItemTeeth[] = [];
  isLoading = false;

  constructor(private readonly dialogRef: MatDialogRef<TdoOrganizerCaseToothDialogComponent>,
              private readonly el: ElementRef,
              @Inject(MAT_DIALOG_DATA) private data: DocumentInFolderDataModel,
              private readonly fileManagerService: FileManagerService,
              private readonly globals: Globals,
              private readonly landingPageService: LandingPageService,
              private readonly cdr: ChangeDetectorRef,
              private readonly uiService: UiService) { }

  ngOnInit(): void {
    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      cases: this.landingPageService.getPatientsCases4Landing(this.globals.currentPatientId),
      caseAndTeeth: this.fileManagerService.getCaseTeethForDocument(this.data.dataDocument)
    }).pipe(
      finalize(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe(({cases, caseAndTeeth}) => {
      this.cases = cases;
      this.currentCaseAndTeeth = caseAndTeeth;
      this.selectedCaseWorkItemTeethIds = caseAndTeeth.teeth.map(p => ({ selectedCaseWorkItemTeethId: p.CaseWorkItemToothId, ToothID: p.ToothID }));
      this.selectedConfigurationTeeth = this.globals.teeth.filter(p => caseAndTeeth.teeth.map(g => g.ToothID).includes(p.ToothId));
      const cse = cases.find(p => p.PatientCaseId === caseAndTeeth.patientCaseId);
      if (cse) {
        cse.TeethConfigurations = this.globals.teeth.filter(p => cse.TeethIdsList.includes(p.ToothId));
        this.selectedCase = cse;
        setTimeout(() => {
          const selectedEl = this.el.nativeElement.querySelector('.case-tooth-dialog-item-selected');
          if (selectedEl) {
            selectedEl.scrollIntoView();
          }
        }, 200);
      }
    }, (err: any) => {
      this.uiService.displayMessage('error', 'Error loading data');
      this.isLoading = false;
    });
  }

  onCancelClick() {
    this.dialogRef.close(false);
  }

  onOkClick() {
    if (!this.selectedCase) {
      return;
    }
    this.isLoading = true;
    const model = new DocumentSaveCaseTeethModel();
    model.documentId = this.data.dataDocument.DocumentID;
    model.patientId = this.data.dataDocument.PatientId;
    model.parentDocumentId = this.data.dataDocument.ParentDocumentID;
    model.oldPatientCaseId = this.currentCaseAndTeeth.patientCaseId;
    model.newPatientCaseId = this.selectedCase.PatientCaseId;
    model.selectedCaseWorkItemTeeth = this.selectedCaseWorkItemTeethIds;
    this.fileManagerService.saveCaseTooth(model).subscribe({
      next: (res) => {
      this.isLoading = false;
      this.dialogRef.close(true);
    },
      error: (error) => {
      this.isLoading = false;
      this.cdr.markForCheck();
      console.error(error);
    }});
  }

  onCaseClick(cse: PatientCaseViewDataModel) {// when case lenght = 1 but not original included to new one
    this.selectedCase = cse;
    this.selectedCaseWorkItemTeethIds = [];
    // If selecting through the list, you land on the documents current case, auto select the teeth, otherwise blank it out.
    if (this.selectedCase.PatientCaseId === this.currentCaseAndTeeth.patientCaseId) {
      this.selectedCaseWorkItemTeethIds.push(...this.currentCaseAndTeeth.teeth.map(p => ({ selectedCaseWorkItemTeethId: p.CaseWorkItemToothId, ToothID: p.ToothID })));
    } else if (this.selectedCase.TeethList.length === 1) {
      this.selectedCaseWorkItemTeethIds = cse.DataTeethIdsList.map(p => ({ selectedCaseWorkItemTeethId: p.CaseWorkItemToothId, ToothID: p.ToothID }));
    }
  }

  onCheckboxChange($event: MatCheckboxChange, tooth: DocumentPseudoPatientCaseWorkItemToothDataModel, cb: MatCheckbox) {
    if ($event.checked) {
      if (!this.selectedCaseWorkItemTeethIds.some(x => x.ToothID === tooth.ToothID)) {
        const selectedTooth = new SelectedCaseWorkItemTeeth();
        selectedTooth.selectedCaseWorkItemTeethId = tooth.CaseWorkItemToothId;
        selectedTooth.ToothID = tooth.ToothID;
        this.selectedCaseWorkItemTeethIds.push(selectedTooth);
      }
    } else {
      this.selectedCaseWorkItemTeethIds = this.selectedCaseWorkItemTeethIds.filter(x => x.ToothID !== tooth.ToothID);
    }
  }

  isChecked(tooth: DocumentPseudoPatientCaseWorkItemToothDataModel) {
    if (!this.selectedCase) {
      return false;
    }
    if (this.selectedCase.DataTeethIdsList.length === 1 || this.selectedCaseWorkItemTeethIds.some(x => x.ToothID === tooth.ToothID)) {
      return true;
    }
    return false;
  }
}
